import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { SystemNotificationService, HolidayService, EmployeeService, DealService } from 'app/api/services';
import { AppAuthService } from 'app/api/services/cookie-jwt-auth/app-auth.service';
import { SystemNotificationDto, NotificationAction, HolidayDto, EnumViewModel, UserDto, HalfDayType, LeaveType } from 'app/api/models';
import { PurchaseOrderBuilderComponent } from 'app/views/admin/pages/purchase-orders/purchase-order-builder/purchase-order-builder.component';
import { AddItemDialogComponent } from 'app/core/components/add-item-dialog/add-item-dialog.component';
import { sortOnProperty } from 'app/app.component';
import { TimeUtil } from 'app/core/utils/time-utils';
import { HolidayUtilities } from 'app/views/admin/erp/hr/holiday-utilities/holiday-utilities';
import { isNumber } from 'lodash';
import { ScrumboardCardDetailsComponent } from 'app/views/admin/crm/pipeline/pipeline-board/card-details/details.component';
import { QuoteWizardDialogComponent } from 'app/views/admin/crm/pipeline/pipeline-board/quote-wizard-dialog/quote-wizard-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class NotificationSnackbarService {

    allUsers: UserDto[] = [];
    public leaveTypes = Object.values(LeaveType).filter(x => isNumber(x)).map(x => <any> {name: HolidayUtilities.getLeaveTypeName(+x), value: <LeaveType>x});

    constructor(
        private notifService: SystemNotificationService,
        private snackBar: MatSnackBar,
        private appAuth: AppAuthService,
        private dialog: MatDialog,
        private holidayService: HolidayService,
        private employeeService: EmployeeService,
        private dealService: DealService,
    ) {
        this.loadUsers();
    }

    /**
     * Check for new unread notifications and display snackbars
     */
    checkForNewNotifications(): void {
        const user = this.appAuth.getUser();
        if (!user?.id) return;

        this.notifService.apiSystemNotificationForUserGet$Json({
            userId: user.id
        }).subscribe(res => {
            if (!res?.data) return;

            // Filter for unread notifications
            const unreadNotifications = res.data.filter(n => !n.read);
            if (!unreadNotifications.length) return;

            // Show ONE snackbar: the newest unread (API returns newest first). Material only
            // ever displays a single snackbar, so the old "stagger up to 5" loop just replaced
            // the toast every 500ms with progressively OLDER notifications — the message the
            // user read and the 'View' handler that fired could belong to different
            // notifications (e.g. read "Asset Returned to Check", View opened a quote).
            // The full list lives in the notifications panel.
            this.showNotificationSnackbar(unreadNotifications[0]);
        });
    }

    /**
     * Display a snackbar for a notification
     */
    private showNotificationSnackbar(notification: SystemNotificationDto): void {
        const config = {
            duration: 0,  // No auto-dismiss - requires manual action
            horizontalPosition: 'center' as const,
            // Top centre so it doesn't sit over page action buttons; styled at 0.9 opacity
            // (.notification-snackbar in styles.scss) so content behind stays visible.
            verticalPosition: 'top' as const,
            panelClass: 'notification-snackbar'  // Custom styling: primary background, white text
        };

        // Format notification content for better readability
        const message = this.formatNotificationMessage(notification.content || 'New notification');

        const snackBarRef = this.snackBar.open(
            message,
            'View',
            config
        );

        // Handle "View" button click
        snackBarRef.onAction().subscribe(() => {
            this.handleNotificationAction(notification);
        });
    }

    /**
     * Handle navigation based on notification action type
     */
    private handleNotificationAction(notification: SystemNotificationDto): void {
        // Mark as read when user interacts
        notification.read = true;
        this.notifService.apiSystemNotificationUpdateNotificationPut$Json({
            body: notification
        }).subscribe();

        switch (notification.action) {
            case NotificationAction.HolidayToApprove:
                this.openHoliday(notification.targetId);
                break;

            case NotificationAction.PurchaseOrderReview:
                this.openPurchaseOrder(notification.targetId);
                break;

            case NotificationAction.SalesQuoteAdded:
                this.openDeal(notification.targetId);
                break;

            case NotificationAction.AssetReturnedToCheck:
                this.openAsset(notification.targetId);
                break;

            case NotificationAction.QuoteReminder:
            case NotificationAction.QuoteNegotiationAutoReminder:
            case NotificationAction.MachineCalibrationDue:
            case NotificationAction.MachineCalibrationOverdue:
            case NotificationAction.PressureMachineCalibrationDue:
            case NotificationAction.PressureMachineCalibrationOverdue:
            case NotificationAction.ElectricalEquipmentCalibrationDue:
            case NotificationAction.ElectricalEquipmentCalibrationOverdue:
            default:
                // For other notification types, just mark as read
                // Navigation can be added later if needed
                break;
        }
    }

    /**
     * Load users for holiday approval dialog
     */
    private loadUsers(): void {
        this.employeeService.apiEmployeeSearchEmployeeUsersGet$Json$Response().subscribe(res => {
            if (!res?.body.data) return;
            this.allUsers = res.body.data;
        });
    }

    /**
     * Open purchase order builder dialog
     */
    private openPurchaseOrder(purchaseOrderId: number): void {
        this.dialog.open(PurchaseOrderBuilderComponent, {
            data: { purchaseOrderId: purchaseOrderId },
            autoFocus: false,
        });
    }
    
    private openDeal(dealId: number) {
        this.dealService.apiDealDealGet$Json$Response({dealId: dealId}).subscribe(res => {
            if(!res?.body?.data) return;
            
            this.dialog.open(QuoteWizardDialogComponent, {disableClose: true, data: {deal: res.body.data}});
        });
    }
    
    private openAsset(assetId: number) {
        this.dialog.open(ScrumboardCardDetailsComponent, {
            data: { cardId: assetId, isAssetBoard: true },
            autoFocus: false,
        });
    }

    /**
     * Open holiday approval dialog
     */
    private openHoliday(holidayId: number): void {
        this.holidayService.apiHolidayHolidayGet$Json$Response({
            holidayId: holidayId
        }).subscribe(res => {
            if (!res?.body?.data) return;
            this.openHolidayApproval(res.body.data);
        });
    }

    /**
     * Open holiday approval dialog with AddItemDialogComponent
     */
    private openHolidayApproval(currentValue: HolidayDto): void {
        const users = sortOnProperty(this.allUsers.map(x => <EnumViewModel>{name: this.getUserName(x.id), value: x.id}), 'name');
        const halfDayOptions = ['Full Day', 'AM', 'PM'].map((x, i) => <EnumViewModel>{name: x, value: i});

        const dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: `${currentValue ? 'Edit' : 'Add'} Leave`,
                items: [
                    {name: 'user', propertyName: 'userId', type: 'select', options: users, initialValue: currentValue?.userId ?? null, colSpan: 9, mandatory: true},
                    {name: 'private description', propertyName: 'isPrivate', type: 'toggle', initialValue: currentValue?.isPrivate ?? false, colSpan: 3},
                    {name: 'leave type', propertyName: 'leaveType', type: 'select', options: this.leaveTypes, initialValue: currentValue?.leaveType ?? null, colSpan: 12, mandatory: true},

                    {name: 'from', type: 'date', initialValue: currentValue?.from, colSpan: 3, mandatory: true},
                    {name: 'to', type: 'date', initialValue: currentValue?.to, colSpan: 3, mandatory: true},
                    {name: 'time of day', propertyName: 'halfDayType', type: 'select', options: halfDayOptions, initialValue: currentValue?.halfDayType ?? 0, colSpan: 3},
                    {name: 'hours', initialValue: currentValue?.hours ?? 3.5, colSpan: 3, hideConfig: {property: 'halfDayType', value: 0}},

                    {name: 'description', propertyName: 'name', initialValue: currentValue?.name ?? '', colSpan: 12},
                ],
                overrideWidth: 50,
                showDeleteButton: true,
                overrideDeleteText: currentValue?.requiresApproval ? 'Reject' : 'Delete',
                buttons: currentValue?.requiresApproval ? [
                    {name: 'Approve', closeDialog: true, callback: () => this.approveHoliday(currentValue)},
                ] : null,
            }
        });

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            if (dialogRes.delete) {
                this.archiveHoliday(currentValue);
                return;
            }

            const holiday: HolidayDto = {
                name: dialogRes.name,
                leaveType: +dialogRes.leaveType,
                userId: dialogRes.userId,
                from: TimeUtil.setTo1amUTC(new Date(dialogRes.from)).toISOString(),
                to: TimeUtil.setTo1amUTC(new Date(dialogRes.to)).toISOString(),
                days: TimeUtil.getDaysBetween(new Date(dialogRes.from), new Date(dialogRes.to)) + 1,
                isPrivate: dialogRes.isPrivate,
                halfDayType: dialogRes.halfDayType,
                hours: dialogRes.halfDayType == HalfDayType.FullDay ? 7 : (dialogRes.hours || 3.5),
                requiresApproval: currentValue.requiresApproval,
                
                holidayId: currentValue?.holidayId ?? null,
            };

            this.saveHoliday(holiday);
        });
    }

    /**
     * Approve holiday
     */
    private approveHoliday(holiday: HolidayDto): void {
        holiday.requiresApproval = false;
        this.saveHoliday(holiday);
    }

    /**
     * Save holiday
     */
    private saveHoliday(holiday: HolidayDto): void {
        this.holidayService.apiHolidayUpdateHolidayPut$Json$Response({body: holiday}).subscribe(res => {
            if (!res.body.data) return;
        });
    }

    /**
     * Archive holiday
     */
    private archiveHoliday(holiday: HolidayDto): void {
        this.holidayService.apiHolidayArchiveHolidayDelete$Json$Response({holidayId: holiday.holidayId}).subscribe(res => {
            if (!res?.body?.data) return;
        });
    }

    /**
     * Get user name by ID
     */
    private getUserName(userId: number): string {
        if (!this.allUsers) return '';

        for (const u of this.allUsers) {
            if (u.id == userId) return `${u.firstName} ${u.lastName}`;
        }

        return '';
    }

    /**
     * Format notification message for snackbar display
     * Extracts title from <strong> tags and formats with message
     */
    private formatNotificationMessage(html: string): string {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;

        // Try to find title in <strong> tag
        const strongTag = tmp.querySelector('strong');
        let title = '';
        let message = '';

        if (strongTag) {
            title = strongTag.textContent || '';
            // Remove the strong tag and get remaining text
            strongTag.remove();
            message = tmp.textContent?.trim() || '';
        } else {
            // No strong tag, just get all text
            message = tmp.textContent?.trim() || '';
        }

        // Format: "Title - Message" or just "Message" if no title
        if (title && message) {
            return `${title} - ${message}`;
        } else if (title) {
            return title;
        } else {
            return message;
        }
    }
}
